/**
 * Domain Dependencies Context
 *
 * React Context-based dependency injection for domain services.
 * Aligned with api-server dependency injection pattern.
 *
 * Architecture:
 * - Dependencies are cached per orgId (like api-server)
 * - Only loads dependencies for domains that are actually used
 * - Lazy loading of domain dependencies
 * - Type-safe dependency access
 */

import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { getEffectiveOrgId } from "@/services/shared/infrastructure/tenant-state";
import type { DomainDependenciesMap } from "./domain-dependencies.types";
import {
  getCachedDependencies,
  setCachedDependencies,
  updateOrgId,
} from "./domain-dependencies.cache";
import { buildDomainDependencies } from "./domain-dependencies.registry";

/**
 * Domain Dependencies Context
 *
 * Stores domain dependencies per orgId.
 * Similar to api-server's dependency cache.
 */
const DomainDependenciesContext = createContext<DomainDependenciesMap | null>(
  null,
);

/**
 * Domain Dependencies Provider Props
 */
interface DomainDependenciesProviderProps {
  children: React.ReactNode;
}

/**
 * Domain Dependencies Provider
 *
 * Provides domain dependencies via React Context.
 * Dependencies are cached per orgId and lazy-loaded.
 */
export function DomainDependenciesProvider({
  children,
}: DomainDependenciesProviderProps) {
  const [orgId, setOrgId] = useState<string | null>(null);
  const [dependencies, setDependencies] = useState<DomainDependenciesMap>(
    new Map(),
  );

  // Update orgId when tenant changes
  useEffect(() => {
    const currentOrgId = getEffectiveOrgId();
    setOrgId(currentOrgId);

    // Listen for tenant changes
    const interval = setInterval(() => {
      const newOrgId = getEffectiveOrgId();
      if (newOrgId !== orgId) {
        setOrgId(newOrgId);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [orgId]);

  // Update cache when orgId changes
  useEffect(() => {
    if (orgId) {
      updateOrgId(orgId);
    }
  }, [orgId]);

  // Lazy load dependencies as needed
  // Dependencies are built on-demand when useDomainDependencies is called
  // This matches api-server's lazy loading pattern

  const contextValue = useMemo(() => {
    return dependencies;
  }, [dependencies]);

  return (
    <DomainDependenciesContext.Provider value={contextValue}>
      {children}
    </DomainDependenciesContext.Provider>
  );
}

/**
 * Hook to get domain dependencies
 *
 * @param domain - Domain name (e.g., "content", "planning")
 * @returns Domain dependencies or null if not loaded
 */
export function useDomainDependencies<T = any>(domain: string): T | null {
  const depsMap = useContext(DomainDependenciesContext);

  if (!depsMap) {
    return null;
  }

  return (depsMap.get(domain) as T) || null;
}

/**
 * Hook to get all domain dependencies
 *
 * @returns Map of all domain dependencies
 */
export function useAllDomainDependencies(): DomainDependenciesMap | null {
  return useContext(DomainDependenciesContext);
}
